import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from 'app/store/store.hooks';
import { EMealBuilderRole, EProductType, IMenuItem } from 'entities/Menu/Menu.models';
import { ICartAddItemMealBuilderPickParams } from 'entities/Cart/Cart.models';
import { MealBuilderPicksList } from 'entities/Menu/components/MealBuilderPicksList';

interface IMealBuilderItemInfo {
  productType?: 'sandwichOnly' | 'combo' | 'bundle' | 'other';
  includesDrink?: boolean;
  includesSides?: boolean;
  drinkOptions?: 'hot' | 'cold';
  // Franchisor-named default drink/side preselected for a combo (falls back to first when absent).
  defaultDrinkId?: string | null;
  defaultSideId?: string | null;
}

interface IComponentProps {
  menuItem?: IMealBuilderItemInfo;
  mealBuilderPicks: ICartAddItemMealBuilderPickParams[];
  onUpdate: (picks: ICartAddItemMealBuilderPickParams[]) => void;
  // Reports whether the current picks let the item be added to the cart. Invalid when an included
  // combo component has no available selection, or any selected pick is currently unavailable.
  onValidityChange?: (isValid: boolean) => void;
}

export const MealBuilder: FC<IComponentProps> = ({ menuItem, mealBuilderPicks, onUpdate, onValidityChange }) => {
  const { menuMealBuilderPicks } = useAppSelector((state) => state.menu);

  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;
  const mealBuilderPicksRef = useRef(mealBuilderPicks);
  mealBuilderPicksRef.current = mealBuilderPicks;

  const { productType, includesDrink, includesSides, drinkOptions, defaultDrinkId, defaultSideId } = menuItem || {};

  const isCombo = productType === EProductType.Combo;
  const isSandwichOnly = productType === EProductType.SandwichOnly;

  const { allDrinks, drinks, sides } = useMemo(() => {
    const _allDrinks = menuMealBuilderPicks.filter((item) => item.mealBuilderRole === EMealBuilderRole.Drink);
    return {
      allDrinks: _allDrinks,
      drinks: _allDrinks.filter((item) => !drinkOptions || item.drinkTemperature === drinkOptions),
      sides: menuMealBuilderPicks.filter((item) => item.mealBuilderRole === EMealBuilderRole.Side),
    };
  }, [menuMealBuilderPicks, drinkOptions]);

  const showChooseYourDrink = isCombo && includesDrink;
  const showChooseYourSide = isCombo && includesSides;
  const showMakeItAMeal = isSandwichOnly || (isCombo && (!includesDrink || !includesSides));

  const makeItAMealDrinks = useMemo(() => {
    if (isSandwichOnly) return drinks;
    if (isCombo && !includesDrink) return allDrinks;
    return [];
  }, [isSandwichOnly, isCombo, includesDrink, drinks, allDrinks]);

  const makeItAMealSides = useMemo(() => {
    if (isSandwichOnly) return sides;
    if (isCombo && !includesSides) return sides;
    return [];
  }, [isSandwichOnly, isCombo, includesSides, sides]);

  const selectedDrinkId = mealBuilderPicks.find((p) => p.role === EMealBuilderRole.Drink)?.menuItemId;
  const selectedSideId = mealBuilderPicks.find((p) => p.role === EMealBuilderRole.Side)?.menuItemId;

  const isAvailable = (item: IMenuItem) => item.isEnable !== false;

  const firstDrinkId = drinks.find(isAvailable)?.menuItemId;
  const firstSideId = sides.find(isAvailable)?.menuItemId;

  // Preselect the franchisor-named default when it's available in the rubric; otherwise fall back to
  // the first available pick (the default may be temporarily unavailable while the combo stays buildable).
  const defaultDrinkAvailable = !!defaultDrinkId && drinks.some((d) => d.menuItemId === defaultDrinkId && isAvailable(d));
  const defaultSideAvailable = !!defaultSideId && sides.some((s) => s.menuItemId === defaultSideId && isAvailable(s));
  const presetDrinkId = defaultDrinkAvailable ? defaultDrinkId : firstDrinkId;
  const presetSideId = defaultSideAvailable ? defaultSideId : firstSideId;

  // Base price for an included combo component: the franchisor-named default's mealBuilderPrice when set,
  // else the lowest in the rubric (mirrors backend MealBuilderPricingService). The base reads +0; other
  // picks cost the delta (can be negative when the default is not the cheapest).
  const computeBase = (minScope: IMenuItem[], defaultId?: string | null, defaultScope: IMenuItem[] = minScope): number => {
    if (defaultId) {
      const def = defaultScope.find((i) => i.menuItemId === defaultId);
      if (def?.mealBuilderPrice != null) {
        return def.mealBuilderPrice;
      }
    }
    const prices = minScope.map((i) => i.mealBuilderPrice).filter((p): p is number => p != null);
    return prices.length ? Math.min(...prices) : 0;
  };

  const drinkBase = useMemo(() => computeBase(drinks, defaultDrinkId, allDrinks), [drinks, allDrinks, defaultDrinkId]);
  const sideBase = useMemo(() => computeBase(sides, defaultSideId), [sides, defaultSideId]);

  // Included combo component → price relative to the base (default = +0); "Make it a Meal!" → full price.
  const includedDrinkPrice = (item: IMenuItem) => (item.mealBuilderPrice ?? 0) - drinkBase;
  const includedSidePrice = (item: IMenuItem) => (item.mealBuilderPrice ?? 0) - sideBase;

  const resolvePickPrice = (menuItemId: string, role: EMealBuilderRole): number => {
    const list = role === EMealBuilderRole.Drink ? allDrinks : sides;
    const item = list.find((i) => i.menuItemId === menuItemId);
    const mb = item?.mealBuilderPrice ?? 0;
    if (role === EMealBuilderRole.Drink && includesDrink) {
      return mb - drinkBase;
    }
    if (role === EMealBuilderRole.Side && includesSides) {
      return mb - sideBase;
    }
    return mb;
  };

  // An included combo component (Choose Your Drink/Side) is mandatory; "Make it a Meal!" picks are optional.
  const drinkList = showChooseYourDrink ? drinks : makeItAMealDrinks;
  const sideList = showChooseYourSide ? sides : makeItAMealSides;
  const drinkSectionHasAvailable = drinkList.some(isAvailable);
  const sideSectionHasAvailable = sideList.some(isAvailable);

  // A selected pick is "available" only while it is still present and enabled in the live rubric.
  const selectedDrink = allDrinks.find((d) => d.menuItemId === selectedDrinkId);
  const selectedSide = sides.find((s) => s.menuItemId === selectedSideId);
  const selectedDrinkAvailable = !!selectedDrink && isAvailable(selectedDrink);
  const selectedSideAvailable = !!selectedSide && isAvailable(selectedSide);

  useEffect(() => {
    let next = mealBuilderPicksRef.current;

    // Optional section gone fully unavailable → drop its selection (item stays addable without it).
    // A still-selectable section keeps its (possibly unavailable) pick so add-to-cart can block it.
    const filtered = next.filter((p) => {
      if (p.role === EMealBuilderRole.Drink && !showChooseYourDrink && !drinkSectionHasAvailable) return false;
      if (p.role === EMealBuilderRole.Side && !showChooseYourSide && !sideSectionHasAvailable) return false;
      return true;
    });
    if (filtered.length !== next.length) {
      next = filtered;
    }

    // Preselect mandatory included-combo components when nothing is selected yet.
    const additions: ICartAddItemMealBuilderPickParams[] = [];
    if (showChooseYourDrink && presetDrinkId && !next.some((p) => p.role === EMealBuilderRole.Drink)) {
      additions.push({
        menuItemId: presetDrinkId,
        quantity: 1,
        role: EMealBuilderRole.Drink,
        price: resolvePickPrice(presetDrinkId, EMealBuilderRole.Drink),
      });
    }
    if (showChooseYourSide && presetSideId && !next.some((p) => p.role === EMealBuilderRole.Side)) {
      additions.push({
        menuItemId: presetSideId,
        quantity: 1,
        role: EMealBuilderRole.Side,
        price: resolvePickPrice(presetSideId, EMealBuilderRole.Side),
      });
    }
    if (additions.length > 0) {
      next = [...next, ...additions];
    }

    if (next !== mealBuilderPicksRef.current) {
      onUpdateRef.current(next);
    }
  }, [
    showChooseYourDrink,
    showChooseYourSide,
    presetDrinkId,
    presetSideId,
    selectedDrinkId,
    selectedSideId,
    drinkSectionHasAvailable,
    sideSectionHasAvailable,
  ]);

  // Mandatory section → needs an available selection; optional → only blocks when the chosen pick is
  // unavailable. Either failure disables add-to-cart in the parent.
  const drinkValid = showChooseYourDrink
    ? !!selectedDrinkId && selectedDrinkAvailable
    : !selectedDrinkId || selectedDrinkAvailable;
  const sideValid = showChooseYourSide ? !!selectedSideId && selectedSideAvailable : !selectedSideId || selectedSideAvailable;
  const isValid = drinkValid && sideValid;

  useEffect(() => {
    onValidityChangeRef.current?.(isValid);
  }, [isValid]);

  const handleDrinkChange = (menuItemId: string) => {
    onUpdate([
      ...mealBuilderPicks.filter((p) => p.role !== EMealBuilderRole.Drink),
      { menuItemId, quantity: 1, role: EMealBuilderRole.Drink, price: resolvePickPrice(menuItemId, EMealBuilderRole.Drink) },
    ]);
  };

  const handleSideChange = (menuItemId: string) => {
    onUpdate([
      ...mealBuilderPicks.filter((p) => p.role !== EMealBuilderRole.Side),
      { menuItemId, quantity: 1, role: EMealBuilderRole.Side, price: resolvePickPrice(menuItemId, EMealBuilderRole.Side) },
    ]);
  };

  const handleDrinkDeselect = () => {
    onUpdate(mealBuilderPicks.filter((p) => p.role !== EMealBuilderRole.Drink));
  };

  const handleSideDeselect = () => {
    onUpdate(mealBuilderPicks.filter((p) => p.role !== EMealBuilderRole.Side));
  };

  return (
    <div className="menu-details__meal-builder">
      {showChooseYourDrink && (
        <MealBuilderPicksList
          title="Choose Your Drink"
          items={drinks}
          selectedId={selectedDrinkId}
          getPrice={includedDrinkPrice}
          onChange={handleDrinkChange}
        />
      )}

      {showChooseYourSide && (
        <MealBuilderPicksList
          title="Choose Your Side"
          items={sides}
          selectedId={selectedSideId}
          getPrice={includedSidePrice}
          onChange={handleSideChange}
        />
      )}

      {showMakeItAMeal && (
        <div className="menu-details__meal-builder-section">
          <div className="menu-details__meal-builder-title">
            <span>Make it a Meal!</span>
            <span className="fs-xxxs color-orange-main">Optional</span>
          </div>
          <MealBuilderPicksList
            title={<div className="mb-3 fs-xxxs">Add a drink</div>}
            items={makeItAMealDrinks}
            selectedId={selectedDrinkId}
            isOptional
            onChange={handleDrinkChange}
            onDeselect={handleDrinkDeselect}
          />
          <MealBuilderPicksList
            title={<div className="mb-3 fs-xxxs">Add a side</div>}
            items={makeItAMealSides}
            selectedId={selectedSideId}
            isOptional
            onChange={handleSideChange}
            onDeselect={handleSideDeselect}
          />
        </div>
      )}
    </div>
  );
};
